/**
 * Legacy RichText (player 3240) <-> Sign Text (htmlText) converters.
 *
 * A migrated legacy RichText keeps its player id 3240 and its Flash-TextField <Text> blob so the LEGACY
 * Flash player keeps rendering it natively. SignPlayer renders it by converting the blob to htmlText at
 * load. Studio edits it in the rich-text editor, which works in htmlText — so on OPEN we convert the
 * legacy blob to htmlText (flashTextToHtml) and on SAVE we convert back to the legacy blob
 * (htmlToFlashText), keeping the node in the one canonical form both players read.
 *
 * The legacy blob shape (Flash TextField HTML):
 *   <HTML><BODY><P ALIGN="left"><FONT FACE="Arial" SIZE="28" COLOR="#00ff66">text</FONT></P></BODY></HTML>
 * The htmlText shape the editor / RichTextComponent use:
 *   <p><span class="rte-size-28"><span style="color: #00ff66;">text</span></span></p>
 *
 * Fidelity is bounded by what Flash <Text> can express (font face/size/colour, paragraph align, B/I/U);
 * modern-only styling (e.g. background colour) has no legacy equivalent and is dropped on save.
 */

interface RunCtx {
    face: string;
    size: string;
    color: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
}

function baseCtx(): RunCtx {
    return {face: '', size: '', color: '', bold: false, italic: false, underline: false};
}

function htmlEscape(s: string): string {
    return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function attrEscape(s: string): string {
    return htmlEscape(s).replace(/"/g, '&quot;');
}

/** Pull the raw Flash-HTML string out of the store's Text node (string or {_,$} shape). */
export function extractLegacyText(textArr: any): string {
    const t = Array.isArray(textArr) ? textArr[0] : textArr;
    if (t == null) return '';
    if (typeof t === 'string') return t;
    if (typeof t === 'object') return t._ || t['#text'] || '';
    return '';
}

/** Preserve the Text node's attributes (autoScaling) across a save; default to autoScaling="0". */
export function legacyTextAttrs(textArr: any): { [k: string]: string } {
    const t = Array.isArray(textArr) ? textArr[0] : textArr;
    if (t && typeof t === 'object' && t.$) return t.$;
    return {autoScaling: '0'};
}

// ---- forward: legacy Flash <Text> blob -> htmlText (mirror of SignLib RichTextComponent) --------------

export function flashTextToHtml(flashHtml: string): string {
    if (!flashHtml || !flashHtml.trim()) return '';
    try {
        const doc = new DOMParser().parseFromString(flashHtml, 'text/html');
        const parts: string[] = [];
        walkFlashToHtml(doc.body, {face: '', size: '', color: ''}, parts);
        let html = parts.join('');
        if (html.indexOf('<p') === -1) html = '<p>' + html + '</p>';
        return html;
    } catch {
        const plain = (flashHtml || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        return plain ? '<p>' + htmlEscape(plain) + '</p>' : '';
    }
}

function walkFlashToHtml(parent: Node, ctx: {face: string; size: string; color: string}, out: string[]): void {
    if (!parent) return;
    parent.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            const t = child.nodeValue || '';
            if (t) out.push(renderHtmlRun(t, ctx));
            return;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const el = child as Element;
        switch (el.tagName.toUpperCase()) {
            case 'P': {
                const align = (el.getAttribute('align') || '').toLowerCase();
                // Alignment via the `align` ATTRIBUTE, not style="text-align" — the editor's sanitizer strips
                // `style` (same reason as face/colour in renderHtmlRun), which would otherwise reset
                // centre/right/justify paragraphs to left after an edit. `align` is on the allow-list.
                const alignAttr = /^(right|center|justify)$/.test(align) ? ' align="' + align + '"' : '';
                out.push('<p' + alignAttr + '>');
                const before = out.length;
                walkFlashToHtml(el, ctx, out);
                if (out.length === before) out.push('<br/>');
                out.push('</p>');
                break;
            }
            case 'BR':
                out.push('<br/>');
                break;
            case 'FONT': {
                const c2 = {...ctx};
                const f = el.getAttribute('face'); if (f) c2.face = f;
                const s = el.getAttribute('size'); if (s) c2.size = s;
                const col = el.getAttribute('color'); if (col) c2.color = col;
                walkFlashToHtml(el, c2, out);
                break;
            }
            case 'B':
            case 'I':
            case 'U': {
                const tag = el.tagName.toLowerCase();
                out.push('<' + tag + '>');
                walkFlashToHtml(el, ctx, out);
                out.push('</' + tag + '>');
                break;
            }
            default:
                walkFlashToHtml(el, ctx, out);
                break;
        }
    });
}

function renderHtmlRun(rawText: string, ctx: {face: string; size: string; color: string}): string {
    let inner = htmlEscape(rawText);
    // Carry face + colour as <font> ATTRIBUTES, never as inline style. The rich-text editor sanitises its
    // content with Angular's DomSanitizer (SecurityContext.HTML) on load; that allow-list keeps
    // `face`/`color`/`class` but STRIPS `style`. Emitting face/colour via style="font-family/color" lost
    // them on the very first editor load, so the blob saved back by htmlToFlashText came out FACE-less and
    // the legacy Flash field (embedded fonts) rendered BLANK. <font face|color> survives the round-trip.
    // Size stays a class (rte-size-N) so the editor's size dropdown keeps recognising/round-tripping it.
    const attrs: string[] = [];
    if (ctx.face) attrs.push('face="' + attrEscape(ctx.face) + '"');
    if (ctx.color) attrs.push('color="' + attrEscape(ctx.color) + '"');
    if (attrs.length > 0) inner = '<font ' + attrs.join(' ') + '>' + inner + '</font>';
    const px = parseInt(ctx.size, 10);
    if (ctx.size && !isNaN(px) && px > 0) inner = '<span class="rte-size-' + px + '">' + inner + '</span>';
    return inner;
}

// ---- inverse: editor htmlText -> legacy Flash <Text> blob --------------------------------------------

export function htmlToFlashText(html: string): string {
    if (!html || !html.trim()) return '<HTML><BODY></BODY></HTML>';
    let body: HTMLElement;
    try {
        body = new DOMParser().parseFromString(html, 'text/html').body;
    } catch {
        const plain = htmlEscape((html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim());
        return '<HTML><BODY><P ALIGN="left">' + plain + '</P></BODY></HTML>';
    }

    const paras: string[] = [];
    const children = Array.from(body.childNodes);
    const hasBlocks = children.some(n => n.nodeType === Node.ELEMENT_NODE && /^(P|DIV)$/.test((n as Element).tagName));

    if (hasBlocks) {
        children.forEach((n) => {
            if (n.nodeType === Node.ELEMENT_NODE && /^(P|DIV)$/.test((n as Element).tagName)) {
                paras.push(renderFlashParagraph(n as Element, baseCtx()));
            } else if (n.nodeType === Node.TEXT_NODE && (n.nodeValue || '').trim()) {
                const runs: string[] = [];
                walkHtmlToFlash(n, baseCtx(), runs);
                paras.push('<P ALIGN="left">' + runs.join('') + '</P>');
            } else if (n.nodeType === Node.ELEMENT_NODE) {
                // stray inline element at top level -> its own paragraph
                paras.push(renderFlashParagraph(n as Element, baseCtx()));
            }
        });
    } else {
        // no block wrappers -> the whole body is one paragraph
        paras.push(renderFlashParagraph(body, baseCtx()));
    }

    return '<HTML><BODY>' + paras.join('') + '</BODY></HTML>';
}

function renderFlashParagraph(el: Element, ctx: RunCtx): string {
    const align = paragraphAlign(el);
    const runs: string[] = [];
    walkHtmlToFlash(el, applyElementCtx(el, ctx), runs);
    return '<P ALIGN="' + align + '">' + runs.join('') + '</P>';
}

function paragraphAlign(el: Element): string {
    const style = (el.getAttribute('style') || '').toLowerCase();
    const m = style.match(/text-align:\s*(left|right|center|justify)/);
    if (m) return m[1];
    const a = (el.getAttribute('align') || '').toLowerCase();
    return /^(left|right|center|justify)$/.test(a) ? a : 'left';
}

function walkHtmlToFlash(parent: Node, ctx: RunCtx, out: string[]): void {
    parent.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            const t = child.nodeValue || '';
            if (t) out.push(renderFlashRun(t, ctx));
            return;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const el = child as Element;
        const tag = el.tagName.toUpperCase();
        if (tag === 'BR') { out.push('<BR>'); return; }
        const c2 = applyElementCtx(el, ctx);
        // nested P/DIV inside a paragraph: flatten its runs inline (rare)
        walkHtmlToFlash(el, c2, out);
    });
}

/** Fold a single element's styling (tag, class, inline style, font attrs) into the run context. */
function applyElementCtx(el: Element, ctx: RunCtx): RunCtx {
    const c: RunCtx = {...ctx};
    const tag = el.tagName.toUpperCase();
    if (tag === 'B' || tag === 'STRONG') c.bold = true;
    if (tag === 'I' || tag === 'EM') c.italic = true;
    if (tag === 'U') c.underline = true;

    if (tag === 'FONT') {
        const face = el.getAttribute('face'); if (face) c.face = face;
        const color = el.getAttribute('color'); if (color) c.color = toHex(color);
        const size = el.getAttribute('size'); if (size && /^\d+$/.test(size)) c.size = size;
    }

    const cls = el.getAttribute('class') || '';
    const cm = cls.match(/rte-size-(\d+)/);
    if (cm) c.size = cm[1];

    const style = el.getAttribute('style') || '';
    const fs = style.match(/font-size:\s*(\d+)\s*px/i); if (fs) c.size = fs[1];
    const col = style.match(/(?:^|;)\s*color:\s*([^;]+)/i); if (col) c.color = toHex(col[1].trim());
    const ff = style.match(/font-family:\s*([^;]+)/i); if (ff) c.face = ff[1].replace(/['"]/g, '').split(',')[0].trim();
    if (/font-weight:\s*(bold|[6-9]00)/i.test(style)) c.bold = true;
    if (/font-style:\s*italic/i.test(style)) c.italic = true;
    if (/text-decoration:[^;]*underline/i.test(style)) c.underline = true;
    return c;
}

function renderFlashRun(rawText: string, ctx: RunCtx): string {
    let s = htmlEscape(rawText);
    let attrs = '';
    if (ctx.face) attrs += ' FACE="' + attrEscape(ctx.face) + '"';
    if (ctx.size) attrs += ' SIZE="' + ctx.size + '"';
    if (ctx.color) attrs += ' COLOR="' + ctx.color + '"';
    let run = attrs ? '<FONT' + attrs + '>' + s + '</FONT>' : s;
    if (ctx.underline) run = '<U>' + run + '</U>';
    if (ctx.italic) run = '<I>' + run + '</I>';
    if (ctx.bold) run = '<B>' + run + '</B>';
    return run;
}

/** Normalise a CSS colour (rgb()/#rgb/#rrggbb) to #rrggbb; empty string if it can't be resolved. */
function toHex(c: string): string {
    c = (c || '').trim();
    const rgb = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgb) {
        return '#' + [rgb[1], rgb[2], rgb[3]].map(x => (+x).toString(16).padStart(2, '0')).join('');
    }
    if (/^#[0-9a-f]{6}$/i.test(c)) return c.toLowerCase();
    if (/^#[0-9a-f]{3}$/i.test(c)) return '#' + c.slice(1).split('').map(ch => ch + ch).join('').toLowerCase();
    return /^#[0-9a-f]+$/i.test(c) ? c.toLowerCase() : '';
}
